/**
 * Email Customization Service
 *
 * All email sending is handled by Cloud Functions triggered by Firestore writes
 * to the emailTokens collection. This service creates tokens that trigger
 * the appropriate Resend-based Cloud Function to send the email.
 *
 * This replaces the previous Firebase Auth native email system with the
 * custom token-based Resend flow for a unified email infrastructure.
 */

import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';

const emailCustomizationService = {
  /**
   * Send a password reset email via the Resend Cloud Function.
   * Creates a password-reset token in emailTokens, which triggers
   * the sendCustomPasswordReset Cloud Function.
   * @param {string} email - Email address to send to
   * @returns {Promise} - Promise that resolves when token is created
   */
  sendCustomPasswordResetEmail: async (email) => {
    try {
      const token = uuidv4();
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours

      const tokenData = {
        token,
        userId: null, // Will be resolved by email lookup in the Cloud Function
        email,
        type: 'password-reset',
        createdAt: new Date(),
        expiresAt,
        used: false
      };

      // Store token in Firestore — this triggers the Cloud Function
      await setDoc(doc(db, 'emailTokens', token), tokenData);

      return {
        success: true,
        message: 'Password reset email sent successfully'
      };
    } catch (error) {
      console.error('Error sending custom password reset email:', error);
      return {
        success: false,
        message: 'Failed to send password reset email',
        error: error.message
      };
    }
  },

  /**
   * Send an email verification via the Resend Cloud Function.
   * Creates an email-verification token in emailTokens, which triggers
   * the sendCustomEmailVerification Cloud Function.
   * @param {object} user - Firebase user object (must have uid and email)
   * @returns {Promise} - Promise that resolves when token is created
   */
  sendCustomEmailVerification: async (user) => {
    try {
      const token = uuidv4();
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours

      const tokenData = {
        token,
        userId: user.uid,
        email: user.email,
        type: 'email-verification',
        createdAt: new Date(),
        expiresAt,
        used: false
      };

      // Store token in Firestore — this triggers the Cloud Function
      await setDoc(doc(db, 'emailTokens', token), tokenData);

      return {
        success: true,
        message: 'Email verification sent successfully'
      };
    } catch (error) {
      console.error('Error sending custom email verification:', error);
      return {
        success: false,
        message: 'Failed to send email verification',
        error: error.message
      };
    }
  },

  /**
   * Send an email change verification via the Resend Cloud Function.
   * Creates an email-change token in emailTokens, which triggers
   * the sendCustomEmailChange Cloud Function.
   * @param {object} user - Firebase user object (must have uid)
   * @param {string} newEmail - New email address to verify
   * @returns {Promise} - Promise that resolves when token is created
   */
  sendCustomEmailChangeVerification: async (user, newEmail) => {
    try {
      const token = uuidv4();
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours

      const tokenData = {
        token,
        userId: user.uid,
        email: newEmail,
        type: 'email-change',
        createdAt: new Date(),
        expiresAt,
        used: false
      };

      // Store token in Firestore — this triggers the Cloud Function
      await setDoc(doc(db, 'emailTokens', token), tokenData);

      return {
        success: true,
        message: 'Email change verification sent successfully'
      };
    } catch (error) {
      console.error('Error sending custom email change verification:', error);
      return {
        success: false,
        message: 'Failed to send email change verification',
        error: error.message
      };
    }
  }
};

export default emailCustomizationService;
